import { useEffect, useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrBefund, MsrBefundliste, MsrBefundschwere } from './typen';
import { z } from './gemeinsam';
import {
  ContentSection, DataTable, EmptyState, Feld, StatusBadge, befundrang, type Befundrang, type Spalte,
} from '../../muster';

/** Schwere → Rang der Befundmarke (TX-STANDARD S1.5); Unbekanntes gilt als Hinweis. */
const RANG: Record<string, Befundrang> = { fehler: 'F', warnung: 'W', hinweis: 'H' };

/** Die Marke eines Befunds - dieselbe Abbildung wie in allen Bahnen (`fachstatus.befundrang`). */
export function befundmarke(schwere: MsrBefundschwere | string) {
  return befundrang(RANG[schwere] ?? 'H');
}

/**
 * Die Befundliste der Gebäudeautomation (Migration 0500, Schritt M2h).
 *
 * Bis zum 16.09.2026 standen die Befunde eines Ableitungslaufs in einem
 * Hinweistext und verschwanden mit dem nächsten Lauf: 392 Feldgeräte ohne ASP,
 * 453 Datenpunkte ohne ASP, 343 über der Leitungslängengrenze — Fließtext,
 * kein Filter, keine Ebene, keine Zahl, an der man sie wiederfindet.
 *
 * Diese Komponente zeigt dieselben Befunde aus `msr_befunde`: entweder für ein
 * Ziel (ASP-Detail, Anlagendetail — dann mit `ziel`) oder projektweit (Reiter
 * „Befunde“, TM-8a). Die Marke kommt seit TM-8a aus `fachstatus.befundrang`
 * (S1.5); die vollständige Liste ohne die Grenze 2.000 steht in der Excel-Mappe
 * „Befunde“ (TM-2b).
 */
export function Befunde({
  api, projektId, ziel, ebene, titel = 'Befunde', leerText = 'Keine Befunde.',
}: {
  api: ApiClient;
  projektId: string;
  ziel?: string;
  ebene?: string;
  titel?: string;
  leerText?: string;
}) {
  const [liste, setListe] = useState<MsrBefundliste | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [schwere, setSchwere] = useState<MsrBefundschwere | ''>('');

  useEffect(() => {
    let aktuell = true;
    setListe(null);
    setFehler(null);

    api.msrBefunde(projektId, { ziel, ebene, schwere: schwere || undefined })
      .then((a) => { if (aktuell) setListe(a); })
      .catch((e) => { if (aktuell) setFehler(e instanceof Error ? e.message : String(e)); });

    return () => { aktuell = false; };
  }, [api, projektId, ziel, ebene, schwere]);

  const spalten: Spalte<MsrBefund>[] = [
    {
      schluessel: 'schwere', titel: 'Schwere', art: 'status', breite: '120px',
      wert: (b) => b.schwere,
      inhalt: (b) => <StatusBadge klein {...befundmarke(b.schwere)} />,
    },
    {
      schluessel: 'regel', titel: 'Regel', wert: (b) => b.regel,
      inhalt: (b) => (
        <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{b.regel}</span>
      ),
    },
    { schluessel: 'kennung', titel: 'Ziel', wert: (b) => b.kennung ?? null },
    {
      schluessel: 'istwert', titel: 'Ist / Grenze', ausrichtung: 'rechts', breite: '130px',
      wert: (b) => b.istwert ?? null,
      inhalt: (b) => (
        <>
          {b.istwert === null || b.istwert === undefined ? '—' : z(b.istwert)}
          {b.grenzwert !== null && b.grenzwert !== undefined && (
            <span className="schwach"> / {z(b.grenzwert)}</span>
          )}
        </>
      ),
    },
    { schluessel: 'text', titel: 'Befund', wert: (b) => b.text, immerSichtbar: true },
  ];

  return (
    <ContentSection
      titel={titel}
      meta={liste
        ? `${z(liste.gesamt)} gesamt · ${z(liste.fehler)} Fehler · `
          + `${z(liste.warnung)} Warnungen · ${z(liste.hinweis)} Hinweise`
        : undefined}
      werkzeuge={(
        <div style={{ minWidth: 200 }}>
        <Feld
          art="auswahl" beschriftung="Schwere"
          wert={schwere}
          beiAenderung={(w) => setSchwere(w as MsrBefundschwere | '')}
          optionen={[
            { wert: '', beschriftung: 'alle Schweren' },
            { wert: 'fehler', beschriftung: 'nur Fehler' },
            { wert: 'warnung', beschriftung: 'nur Warnungen' },
            { wert: 'hinweis', beschriftung: 'nur Hinweise' },
          ]}
        />
        </div>
      )}
    >
      <DataTable
        name={titel}
        spalten={spalten}
        zeilen={liste?.zeilen ?? []}
        schluessel={(b) => b.id}
        dicht
        ausfuhr
        ausfuhrDateiname="msr-befunde"
        laedt={!liste && !fehler}
        fehler={fehler ?? undefined}
        beiErneutVersuchen={() => setSchwere((x) => x)}
        leer={(
          <EmptyState
            titel={leerText}
            erklaerung={schwere
              ? 'Mit dieser Schwere gibt es keinen Befund — ohne Filter stehen '
                + 'möglicherweise andere in der Liste.'
              : 'Der letzte Ableitungslauf hat hier nichts zu beanstanden gefunden.'}
            aktion={schwere ? (
              <button type="button" className="m-knopf" onClick={() => setSchwere('')}>
                Filter zurücksetzen
              </button>
            ) : undefined}
          />
        )}
      />

      {liste && liste.zeilen.length >= 2000 && (
        <p className="schwach" style={{ fontSize: 11 }}>
          Nur die ersten 2.000 Befunde — nach Schwere filtern; vollständig in der Excel-Mappe „Befunde“
          (Reiter Befunde, Kopf der Seite).
        </p>
      )}
    </ContentSection>
  );
}
